import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SparklesIcon, DocumentTextIcon, PlayIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { aiAPI } from '../utils/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const AIGeneratorPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const boardContext = location.state;
  
  const [formData, setFormData] = useState({
    platform: 'youtube',
    requirements: '',
    boardId: boardContext?.boardId || '',
  });
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    if (boardContext) {
      setFormData(prev => ({
        ...prev,
        boardId: boardContext.boardId,
        requirements: `Create content for my "${boardContext.boardName}" board. ` + prev.requirements
      }));
    }
  }, [boardContext]);

  const platforms = [
    { id: 'youtube', name: 'YouTube', description: 'Long-form video scripts' },
    { id: 'instagram', name: 'Instagram', description: 'Short-form reels and posts' },
    { id: 'tiktok', name: 'TikTok', description: 'Viral short videos' },
    { id: 'linkedin', name: 'LinkedIn', description: 'Professional content' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await aiAPI.generateScript(formData);
      setGeneratedContent(response.data.generatedContent);
      toast.success('Content generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSaveToBoard = async () => {
    if (!boardContext?.boardId || !generatedContent) {
      toast.error('No board or content to save');
      return;
    }

    setSaveLoading(true);
    try {
      // Save generated content to board using real API
      const contentData = {
        boardId: boardContext.boardId,
        title: `${formData.platform.charAt(0).toUpperCase() + formData.platform.slice(1)} Script - ${new Date().toLocaleDateString()}`,
        description: `AI-generated ${formData.platform} content`,
        platform: formData.platform,
        generatedContent: generatedContent
      };

      const response = await contentAPI.saveGenerated(contentData);
      toast.success('Content saved to board successfully!');
      
      // Navigate back to the board to show the new content
      navigate(`/boards/${boardContext.boardId}`);
    } catch (error) {
      toast.error('Failed to save content to board');
      console.error('Save error:', error);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        {boardContext && (
          <div className="flex items-center justify-center mb-4">
            <button
              onClick={() => navigate(`/boards/${boardContext.boardId}`)}
              className="flex items-center text-primary-600 hover:text-primary-700 mr-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to {boardContext.boardName}
            </button>
          </div>
        )}
        <div className="flex justify-center mb-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-600">
            <SparklesIcon className="h-8 w-8 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          AI Content Generator
        </h1>
        <p className="text-lg text-gray-600">
          {boardContext 
            ? `Generate content for "${boardContext.boardName}" board`
            : 'Generate viral scripts and content for any platform'
          }
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="card"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Content Requirements
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Platform Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Platform
              </label>
              <div className="grid grid-cols-2 gap-3">
                {platforms.map((platform) => (
                  <label
                    key={platform.id}
                    className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                      formData.platform === platform.id
                        ? 'border-primary-600 ring-2 ring-primary-600 bg-primary-50'
                        : 'border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="platform"
                      value={platform.id}
                      checked={formData.platform === platform.id}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className="flex flex-col">
                      <span className="block text-sm font-medium text-gray-900">
                        {platform.name}
                      </span>
                      <span className="block text-sm text-gray-500">
                        {platform.description}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <div>
              <label htmlFor="requirements" className="block text-sm font-medium text-gray-700 mb-2">
                Content Requirements
              </label>
              <textarea
                id="requirements"
                name="requirements"
                rows={6}
                value={formData.requirements}
                onChange={handleChange}
                className="input-field"
                placeholder="Describe what kind of content you want to create. For example: 'Create a viral YouTube script about AI tools that will hook viewers in the first 5 seconds and include a strong call to action.'"
                required
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center py-3"
            >
              {loading ? (
                <LoadingSpinner size="small" />
              ) : (
                <>
                  <SparklesIcon className="w-5 h-5 mr-2" />
                  Generate Content
                </>
              )}
            </button>
          </form>
        </motion.div>

        {/* Generated Content */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="card"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Generated Content
          </h2>
          
          {generatedContent ? (
            <div className="space-y-6">
              {/* Script */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <DocumentTextIcon className="w-5 h-5 mr-2 text-primary-600" />
                  Script
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                    {generatedContent.script}
                  </pre>
                </div>
              </div>

              {/* Titles */}
              {generatedContent.titles && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Title Suggestions
                  </h3>
                  <div className="space-y-2">
                    {generatedContent.titles.map((title, index) => (
                      <div
                        key={index}
                        className="bg-primary-50 border border-primary-200 rounded-lg p-3"
                      >
                        <p className="text-sm font-medium text-primary-900">
                          {title}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hooks */}
              {generatedContent.hooks && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Viral Hooks
                  </h3>
                  <div className="space-y-2">
                    {generatedContent.hooks.map((hook, index) => (
                      <div
                        key={index}
                        className="bg-yellow-50 border border-yellow-200 rounded-lg p-3"
                      >
                        <p className="text-sm text-yellow-900">
                          "{hook}"
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => navigator.clipboard.writeText(generatedContent.script)}
                  className="btn-secondary"
                >
                  Copy Script
                </button>
                {boardContext?.boardId && (
                  <button 
                    onClick={handleSaveToBoard}
                    disabled={saveLoading}
                    className="btn-primary flex items-center"
                  >
                    {saveLoading ? (
                      <LoadingSpinner size="small" />
                    ) : (
                      <>
                        <DocumentTextIcon className="w-4 h-4 mr-2" />
                        Save to {boardContext.boardName}
                      </>
                    )}
                  </button>
                )}
                {!boardContext?.boardId && (
                  <button 
                    onClick={() => navigate('/boards')}
                    className="btn-primary"
                  >
                    Go to Boards
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <PlayIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No content generated yet
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Fill out the form and click generate to create AI content.
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* AI Generation Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="bg-green-50 border border-green-200 rounded-lg p-4"
      >
        <div className="flex">
          <div className="flex-shrink-0">
            <SparklesIcon className="h-5 w-5 text-green-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Real AI Generation Powered by Cloudflare Workers AI
            </h3>
            <div className="mt-2 text-sm text-green-700">
              <p>
                Generate high-quality content using advanced AI models. Your generated content can be saved directly to your boards.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AIGeneratorPage; 